import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useRef,
  useState,
  useMemo,
  startTransition,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { Column, ScrollTabs } from 'components/fragment';
import { ScrollTabsProps, Option } from 'components/fragment/ScrollTabs/ScrollTabs.props';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import {
  programColumnsSelector,
  hasInitializedColumnsSelector,
} from 'store/programs/programs.selector';
import { useUpdateDashboardSettings, updateProgramsHistoryArray } from '@/utils';
import { useMediaQuery } from '@react-hook/media-query';
import { BREAKPOINT_700PX } from '@/common/const/breakpoints';
import Carousel from 'react-multi-carousel';
import Marquee from 'react-fast-marquee';
import 'react-multi-carousel/lib/styles.css';
import CloseIcon from '@mui/icons-material/Close';
import {
  ETIDialog,
  ETIDialogHeader,
  ETIDialogBody,
  ETIDialogFooter,
  IconButton,
  Button,
} from '@zawarski/eti-typescript-components';
import { DropSelect } from '@zawarski/eti-ui';
import { CarouselNav } from 'components/primitives';
import {
  isMobile as isMobileDeviceDetect,
  // isTablet as isTabledDeviceDetect,
} from 'react-device-detect';

import {
  ADD_COLUMNS,
  REMOVE_COLUMN,
  SPLIT_COLUMN,
  FULL_COLUMN,
  PIN_COLUMN,
  ADD_PROGRAM,
  REMOVE_PROGRAM,
} from 'store/programs/programs.actions';

import { UPDATE_DASHBOARD_SETTINGS } from 'store/account/account.actions';
import { accountSelector, programsPermissionsSelector } from 'store/account/account.selector';
import { PROGRAMS_NAMES } from 'data/index';
import {
  progInFullScreenSelector,
  programsListWSUpdatesSelector,
  selectNotifications,
} from 'store/app/app.selector';
import {
  REMOVE_PROGRAM_WS_UPDATES,
  SET_PROG_IN_FULLSCREEN,
  SET_PROGRAM_WS_UPDATES,
  SET_HIGHLIGHTED_DATE,
  RESET_HIGHLIGHTED_DATES,
} from 'store/app/app.actions';
// import useWebSocket from 'hooks/useWebSocket';
// import { jwtSelector } from 'store/auth/auth.selector';

interface IGridLayout {
  onBoardingSettings: {
    isInitialized: boolean;
    hasUpdates: boolean;
    hasCheckedHide: boolean;
    programInFullScreen: string;
  };
  programsHistory: string[];
  programsList: Nullable<ISelectedPrograms[]>;
  notificationType: string;
}

export interface GridLayoutRef {
  onProgramFullScreen: (progId?: string) => boolean;
}

const GridLayout = forwardRef<GridLayoutRef, IGridLayout>(
  ({ onBoardingSettings, programsHistory, programsList, notificationType }, ref) => {
    const dispatch = useAppDispatch();
    const columns = useAppSelector(programColumnsSelector);
    const programIds = Object.values(columns).flat();
    const containerRef = useRef<HTMLDivElement>(null as unknown as HTMLDivElement);
    const [dynamicSize, setDynamicSize] = useState(317);
    const [hasScroll, setHasScroll] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedProgram, setSelectedProgram] = useState<string>('');
    const [selectedColumn, setSelectedColumn] = useState<string>('');
    const [selectedWidgetIndex, setSelectedWidgetIndex] = useState<number>(0);

    const progInFullScreen = useAppSelector(progInFullScreenSelector);
    const notifications = useAppSelector(selectNotifications);
    const [selectedTabIndex, setSelectedTabIndex] = useState<number>(-1);
    const [tabOptions, setTabOptions] = useState<Option[]>([]);
    const [marquee, setMarquee] = useState<{ pvDataID: number; message: string }[]>([]);

    const [shouldUpdateSettings, setShouldUpdateSettings] = useState<{
      shouldUpdateSelectedPrograms: boolean;
      shouldUpdateProgInFullScreen: boolean;
      shouldUpdateProgramsHistory: boolean;
      programId?: string;
    }>({
      shouldUpdateSelectedPrograms: false,
      shouldUpdateProgInFullScreen: false,
      shouldUpdateProgramsHistory: false,
      programId: '',
    });

    const [quickPickList, setQuickPickList] = useState<string[]>([]);
    const hasInitializedColumns = useAppSelector(hasInitializedColumnsSelector);
    const programsPermissions = useAppSelector(programsPermissionsSelector);
    const delayedSettingsUpdate = useUpdateDashboardSettings();
    const userProgramsListWSUpdates = useAppSelector(programsListWSUpdatesSelector);
    //filter the programs that are no longer selected from the userProgramsListWSUpdates
    const filteredUpdatedPrograms = useMemo(
      () =>
        userProgramsListWSUpdates.filter((programId) => !programIds.includes(programId.toString())),
      [userProgramsListWSUpdates, programIds]
    );

    const account = useAppSelector(accountSelector) || undefined;
    const token = account?.accessToken || '';
    // const jwt = useAppSelector(jwtSelector);
    const userId = account?.id || 0;

    const filteredPrograms = React.useMemo(() => {
      if (!Array.isArray(programsPermissions) || programsPermissions.length === 0) {
        return [];
      }

      return PROGRAMS_NAMES.filter((program) =>
        programsPermissions.some((permission) => Number(program.value) === permission.pvServiceID)
      );
    }, [programsPermissions]);

    const availableOptions = React.useMemo(() => {
      return filteredPrograms
        .map((program) => {
          const isAvailable = Object.values(columns).flat().includes(program.value);
          return isAvailable ? null : program;
        })
        .filter((program): program is { label: string; value: string } => program !== null)
        .sort((a, b) => a.label.localeCompare(b.label));
    }, [filteredPrograms, columns]);
    const isNarrowView = useMediaQuery(`only screen and (max-width: ${BREAKPOINT_700PX})`);

    useImperativeHandle(ref, () => ({
      onProgramFullScreen,
    }));

    useEffect(() => {
      if (notificationType === 'toast' && notifications.length) {
        notifications.forEach((notification) => {
          if (!notification.showToast) return;
          toast.success(`${notification.pvDescription}`);
        });
      }
    }, [notifications, notificationType]);

    useEffect(() => {
      if (notifications.length) {
        const newMarquee = notifications.map((notification) => ({
          pvDataID: notification.pvDataID,
          message: notification.pvDescription,
        }));
        setMarquee(newMarquee);
      }
    }, [notifications]);

    useEffect(() => {
      if (!filteredUpdatedPrograms.length) return;
      filteredUpdatedPrograms.forEach((progID) => dispatch(REMOVE_PROGRAM_WS_UPDATES(progID)));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredUpdatedPrograms]);

    useEffect(() => {
      if (!programsList || !programsHistory) return;

      // Get active program IDs
      const selectedPrograms = new Set(
        programsList.filter((program) => program.isActive).map((program) => program.id.toString())
      );

      // Get all program IDs from programsList to check for existence
      const validProgramIds = new Set(programsList.map((program) => program.id.toString()));

      // Filter programsHistory
      const quickPickList = programsHistory.filter(
        (program) =>
          (!selectedPrograms.has(program) || program === '45') && validProgramIds.has(program)
      );

      setQuickPickList(quickPickList);
    }, [programsList, programsHistory]);

    useEffect(() => {
      if (progInFullScreen && hasInitializedColumns) {
        setSelectedTabIndex(
          programIds
            .filter((id) => id !== 'widget')
            .findIndex((id) => id === progInFullScreen.toString())
        );
      }
    }, [programIds, progInFullScreen, hasInitializedColumns]);

    useEffect(() => {
      if (
        !shouldUpdateSettings.shouldUpdateSelectedPrograms &&
        !shouldUpdateSettings.shouldUpdateProgInFullScreen
      ) {
        return;
      }
      const onBoarding = { ...onBoardingSettings };
      if (shouldUpdateSettings.shouldUpdateProgInFullScreen) {
        onBoarding.programInFullScreen = shouldUpdateSettings.programId || '';
      }

      const shouldUpdateHistory =
        shouldUpdateSettings.shouldUpdateSelectedPrograms &&
        shouldUpdateSettings.shouldUpdateProgramsHistory;

      const history = shouldUpdateHistory
        ? updateProgramsHistoryArray([selectedProgram], programsHistory)
        : null;

      const updatedSettings: DashboardSettingsUpdate = {
        selectedPrograms:
          shouldUpdateSettings.shouldUpdateSelectedPrograms && Object.keys(columns).length > 0
            ? columns
            : undefined,
        onBoarding: shouldUpdateSettings.shouldUpdateProgInFullScreen ? onBoarding : undefined,
        programsHistory: shouldUpdateHistory ? history : undefined,
      };
      // Remove undefined keys to avoid unnecessary updates
      Object.keys(updatedSettings).forEach(
        (key) => updatedSettings[key] === undefined && delete updatedSettings[key]
      );
      if (Object.keys(updatedSettings).length > 0) {
        dispatch(UPDATE_DASHBOARD_SETTINGS(updatedSettings));
      }

      delayedSettingsUpdate();
      resetShouldUpdateSettings();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, shouldUpdateSettings, dispatch, onBoardingSettings]);

    function resetShouldUpdateSettings() {
      setShouldUpdateSettings({
        shouldUpdateSelectedPrograms: false,
        shouldUpdateProgInFullScreen: false,
        shouldUpdateProgramsHistory: false,
        programId: '',
      });
    }

    const responsive = {
      superLargeDesktop: {
        breakpoint: { max: 4000, min: 3000 },
        items: 5,
      },
      desktop: {
        breakpoint: { max: 3000, min: 1024 },
        items: 3,
      },
      tablet: {
        breakpoint: { max: 1024, min: 464 },
        items: 2,
      },
      mobile: {
        breakpoint: { max: 700, min: 0 },
        items: 1,
      },
    };

    // Consolidated resize and scroll handlers into single effect
    useEffect(() => {
      const updateWidth = () => {
        if (containerRef.current) {
          let dynSize = 0;
          const scrollColumnsArr = Array.from(containerRef.current.children);
          const maxChildWidth = scrollColumnsArr
            .map((child) => (child as HTMLElement).offsetWidth)
            .reduce((max, width) => Math.max(max, width), 317); // Min width 317px

          if (scrollColumnsArr.length > 1) {
            dynSize = maxChildWidth;
          }

          setDynamicSize(dynSize);
        }
      };

      const handleResize = () => {
        updateWidth();
      };

      const handleScroll = () => {
        if (containerRef.current) {
          setHasScroll(containerRef.current.scrollLeft > 0);
        }
      };

      // Run once on mount
      handleResize();
      handleScroll();

      // Single resize listener handles both window size and width updates
      window.addEventListener('resize', handleResize);

      const currentContainer = containerRef.current;
      if (currentContainer) {
        currentContainer.addEventListener('scroll', handleScroll);
      }

      return () => {
        window.removeEventListener('resize', handleResize);
        if (currentContainer) {
          currentContainer.removeEventListener('scroll', handleScroll);
        }
      };
    }, [columns]);

    useEffect(() => {
      const scrollTabsOptions: Option[] = [];
      // const marqueeMsg: { programId: string; message: string }[] = [];

      Object.entries(columns).map(([columnId, column]: [string, string[]]) => {
        column
          .filter((value) => !value.includes('widget'))
          .forEach((value, columnIndex) => {
            // if (value.includes('widget')) {
            //   scrollTabsOptions.push({ value: '0', label: 'Empty', columnId, columnIndex });
            // }
            const foundProgram = PROGRAMS_NAMES.find((p) => p.value === value);
            if (foundProgram) {
              const hasUpdates =
                progInFullScreen !== foundProgram.value &&
                userProgramsListWSUpdates.includes(Number(foundProgram.value));

              scrollTabsOptions.push({
                value: foundProgram.value,
                label: foundProgram.label || 'No Name Set',
                columnId,
                columnIndex,
                hasUpdates,
              });
            }
          });
      });

      const newColumnId = `column${Object.keys(columns).length + 1}`;
      scrollTabsOptions.push({
        value: '-1',
        label: 'Add program',
        columnId: newColumnId,
        columnIndex: 0,
      });

      setTabOptions((prev) => {
        // Compare by length and values instead of JSON.stringify for better performance
        if (prev.length !== scrollTabsOptions.length) return scrollTabsOptions;
        const hasChanged = scrollTabsOptions.some(
          (opt, i) =>
            opt.value !== prev[i]?.value ||
            opt.hasUpdates !== prev[i]?.hasUpdates ||
            opt.columnId !== prev[i]?.columnId
        );
        return hasChanged ? scrollTabsOptions : prev;
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userProgramsListWSUpdates, columns, programIds]);

    const addColumn = () => {
      const newColumnId = `column${uuidv4()}`;
      const payload = { [newColumnId]: [`widget`] };
      dispatch(ADD_COLUMNS(payload));
      setShouldUpdateSettings({
        shouldUpdateSelectedPrograms: true,
        shouldUpdateProgInFullScreen: false,
        shouldUpdateProgramsHistory: false,
        programId: '',
      });
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.scrollLeft = containerRef.current.scrollWidth;
        }
      }, 0);
    };

    const removeColumn = (columnId: string) => {
      dispatch(REMOVE_COLUMN(columnId));
    };

    const addWidget = (columnId: string, index: number) => {
      setDialogOpen(true);
      setSelectedColumn(columnId);
      setSelectedWidgetIndex(index);
    };

    const splitWidget = (columnId: string) => {
      dispatch(SPLIT_COLUMN(columnId));
    };

    const fullColumn = (columnId: string) => {
      dispatch(FULL_COLUMN(columnId));
    };

    const pinColumn = (columnId: string) => {
      dispatch(PIN_COLUMN(columnId));
      setShouldUpdateSettings({
        shouldUpdateSelectedPrograms: true,
        shouldUpdateProgInFullScreen: false,
        shouldUpdateProgramsHistory: false,
        programId: '',
      });
    };

    const handleDialogClose = () => {
      setDialogOpen(false);
    };

    const onAddProgram = (programId?: string, columnId?: string, index?: number) => {
      //special case for program id 45 Dot Video Wall
      const progID = programId || selectedProgram;
      if (Number(progID) === 45) {
        const url = `https://www.palmettoeoc.com/app/dotcameraboard?access_token=${token}&userId=${userId}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        const payload = {
          columnId: columnId ? columnId : selectedColumn,
          widgetIndex: index ? index : selectedWidgetIndex,
          programId: progID,
        };
        startTransition(() => {
          setShouldUpdateSettings({
            shouldUpdateSelectedPrograms: true,
            shouldUpdateProgInFullScreen: !!progInFullScreen,
            shouldUpdateProgramsHistory: true,
            programId: progID,
          });
          if (programId) setSelectedProgram(programId);
          dispatch(ADD_PROGRAM(payload));
        });
      }
    };

    const handleDialogSave = () => {
      setDialogOpen(false);
      // Check if selectedColumn already exists - if so, add program to it
      // Otherwise, create a new column first
      if (columns[selectedColumn]) {
        onAddProgram(selectedProgram, selectedColumn, selectedWidgetIndex);
      } else {
        const newColumnId = `column${uuidv4()}`;
        const payload = { [newColumnId]: [`widget`] };
        dispatch(ADD_COLUMNS(payload));
        onAddProgram(selectedProgram, newColumnId, 0);
      }
    };

    const clearAllNotifications = () => {
      dispatch(SET_PROGRAM_WS_UPDATES([]));
    };

    const onProgramRemove = (programId: string) => {
      let columnId = '';
      let willRemoveColumn = true;

      for (const [key, value] of Object.entries(columns)) {
        if (value.includes(programId)) {
          const otherValues = value.filter((v) => v !== programId);
          if (otherValues.length > 0 && !otherValues.includes('widget')) {
            willRemoveColumn = false;
          }
          columnId = key;
          break;
        }
      }

      // find the index of programId in tabOptions and get the value of the previous index. if there is no other previous element check the index after the found index of program id
      const foundIndex = tabOptions.findIndex((option) => option.value === programId);
      const previousIndex = foundIndex > 0 ? foundIndex - 1 : foundIndex + 1;
      const previousProgramId = tabOptions[previousIndex]?.value;
      // check if the program removed is pinned, if so set the columnId of the next program as pinned
      const pinnedProgram = tabOptions[foundIndex].columnId === 'pinnedColumn';
      if (pinnedProgram && Number(previousProgramId) !== -1) {
        const columnToBePinned = tabOptions[previousIndex].columnId;
        dispatch(PIN_COLUMN(columnToBePinned));
      }

      startTransition(() => {
        dispatch(REMOVE_PROGRAM_WS_UPDATES(Number(programId)));

        // if we have a program in fullscreen we set the next program in fullscreen if we have one
        setShouldUpdateSettings({
          shouldUpdateSelectedPrograms: true,
          shouldUpdateProgInFullScreen: true,
          shouldUpdateProgramsHistory: false,
          programId:
            progInFullScreen && previousProgramId && Number(previousProgramId) !== -1
              ? previousProgramId
              : '',
        });

        dispatch(REMOVE_PROGRAM(programId));
        if (!willRemoveColumn) {
          fullColumn(columnId);
        }

        // setMarquee((prev) => prev.filter((msg) => msg.programId !== programId));
        if (programId === progInFullScreen) {
          dispatch(
            SET_PROG_IN_FULLSCREEN(
              previousProgramId && Number(previousProgramId) !== -1 ? previousProgramId : ''
            )
          );
        }
      });
    };

    const onProgramFullScreen = (_progId?: string) => {
      const programID =
        progInFullScreen || columns['pinnedColumn'][0] !== 'widget'
          ? columns['pinnedColumn'][0]
          : columns['pinnedColumn'][1] !== 'widget'
            ? columns['pinnedColumn'][1]
            : '';

      if (!programID) return false;
      const isOpen = !!progInFullScreen;
      if (isOpen) {
        setShouldUpdateSettings(() => ({
          shouldUpdateSelectedPrograms: false,
          shouldUpdateProgInFullScreen: true,
          shouldUpdateProgramsHistory: false,
          programId: '',
        }));
        clearAllNotifications();
        dispatch(SET_PROG_IN_FULLSCREEN(''));
      } else {
        setShouldUpdateSettings(() => ({
          shouldUpdateSelectedPrograms: false,
          shouldUpdateProgInFullScreen: true,
          shouldUpdateProgramsHistory: false,
          programId: programID,
        }));
        setSelectedTabIndex(() => programIds.findIndex((id) => id === programID));
        dispatch(SET_PROG_IN_FULLSCREEN(programID));
        dispatch(RESET_HIGHLIGHTED_DATES());
        clearAllNotifications();
      }
      return !isOpen;
    };

    const onChangeTab = (programId: string, columnId: string, columnIndex: number) => {
      //set the new highlighted date for the program in full screen before changing tab
      const date = new Date().toISOString();
      dispatch(SET_HIGHLIGHTED_DATE({ programId: progInFullScreen, date }));
      dispatch(REMOVE_PROGRAM_WS_UPDATES(Number(progInFullScreen)));

      const progNumber = Number(programId);
      if (progNumber === 0 || progNumber === -1) {
        addWidget(columnId, columnIndex);
      } else {
        dispatch(REMOVE_PROGRAM_WS_UPDATES(progNumber));
        setShouldUpdateSettings({
          shouldUpdateSelectedPrograms: false,
          shouldUpdateProgInFullScreen: true,
          shouldUpdateProgramsHistory: false,
          programId: programId,
        });
        dispatch(SET_PROG_IN_FULLSCREEN(programId));
      }
    };

    const combinedProps: ScrollTabsProps = {
      options: tabOptions,
      selectedTabIndex,
      onChangeTab,
      setSelectedTabIndex,
      isFullScreen: !!progInFullScreen,
      programIdInFullScreen: progInFullScreen,
      onProgramRemove,
    };

    //only show fixed column if there is at least one program in the pinned column or if there is no other selected program across all columns
    const showFixed =
      columns['pinnedColumn']?.some((value) => value !== 'widget') ||
      !programIds?.some((value) => value !== 'widget');

    return (
      <>
        <div className='h-screen overflow-hidden'>
          {progInFullScreen && !isMobileDeviceDetect && <ScrollTabs {...combinedProps} />}
          {isMobileDeviceDetect || isNarrowView ? (
            <div className={'relative h-full'}>
              <Carousel
                className='h-full'
                arrows={false}
                renderButtonGroupOutside={true}
                responsive={responsive}
                customButtonGroup={<CarouselNav addColumn={addColumn} />}>
                {Object.entries(columns).map(([columnId, column]: [string, string[]], index) => {
                  const isLastColumn = Object.entries(columns).slice(-1)[0][0] === columnId;
                  return (
                    <div style={{ height: 'calc(100% - 60px)' }}>
                      <Column
                        key={columnId}
                        column={column}
                        columnId={columnId}
                        index={index + 1}
                        isLastColumn={isLastColumn}
                        onAddWidget={addWidget}
                        onAddColumn={addColumn}
                        onRemoveColumn={removeColumn}
                        onSplit={splitWidget}
                        onFullColumn={fullColumn}
                        onPin={pinColumn}
                        isMobile={isMobileDeviceDetect || isNarrowView}
                        isFixed={index === 0}
                        hasScroll={hasScroll}
                        onProgramRemove={onProgramRemove}
                        onProgramFullScreen={onProgramFullScreen}
                        quickPickList={quickPickList}
                        onAddProgram={onAddProgram}
                        dynamicSize={dynamicSize}
                        notificationType={notificationType}
                      />
                    </div>
                  );
                })}
              </Carousel>
            </div>
          ) : (
            <div className='flex h-full'>
              {/* Fixed first column */}
              {showFixed &&
                Object.entries(columns)
                  .slice(0, 1)
                  .map(([columnId, column]: [string, string[]], index) => {
                    const isLastColumn = Object.entries(columns).slice(-1)[0][0] === columnId;
                    return (
                      <Column
                        key={columnId}
                        column={column}
                        columnId={columnId}
                        index={index}
                        isFixed={true}
                        isLastColumn={isLastColumn}
                        onAddWidget={addWidget}
                        onAddColumn={addColumn}
                        onRemoveColumn={removeColumn}
                        onSplit={splitWidget}
                        onFullColumn={fullColumn}
                        onPin={pinColumn}
                        hasScroll={hasScroll}
                        isMobile={isMobileDeviceDetect}
                        onProgramRemove={onProgramRemove}
                        onProgramFullScreen={onProgramFullScreen}
                        programIdInFullScreen={progInFullScreen}
                        quickPickList={quickPickList}
                        onAddProgram={onAddProgram}
                        dynamicSize={dynamicSize}
                        notificationType={notificationType}
                      />
                    );
                  })}
              {/* Scrollable container for other columns */}
              <div
                ref={containerRef}
                className='relative flex h-full'
                style={{
                  width: dynamicSize ? `calc(100% - ${dynamicSize}px)` : '50%',
                  marginLeft: dynamicSize && showFixed ? `${dynamicSize}px` : showFixed ? '50%' : 0,
                  overflow: 'scroll',
                }}>
                {Object.entries(columns)
                  .slice(1)
                  .map(([columnId, column]: [string, string[]], index) => {
                    const isLastColumn = Object.entries(columns).slice(-1)[0][0] === columnId;
                    return (
                      <Column
                        key={columnId}
                        column={column}
                        columnId={columnId}
                        index={index + 1}
                        isLastColumn={isLastColumn}
                        onAddWidget={addWidget}
                        onAddColumn={addColumn}
                        onRemoveColumn={removeColumn}
                        onSplit={splitWidget}
                        onFullColumn={fullColumn}
                        onPin={pinColumn}
                        isMobile={isMobileDeviceDetect}
                        hasScroll={hasScroll}
                        onProgramRemove={onProgramRemove}
                        onProgramFullScreen={onProgramFullScreen}
                        programIdInFullScreen={progInFullScreen}
                        quickPickList={quickPickList}
                        onAddProgram={onAddProgram}
                        dynamicSize={dynamicSize}
                        notificationType={notificationType}
                      />
                    );
                  })}
              </div>
            </div>
          )}
          ;
          <ETIDialog
            open={dialogOpen}
            maxWidth={'lg'}
            children={[
              <ETIDialogHeader
                key={'etiDialogHeader'}
                title={''}
                titleVariant={'h3'}
                titleColor={'primary'}
                subtitle={''}
                subtitleVariant={'subtitle2'}
                subtitleColor={'#707070'}
                closeButton={
                  <IconButton
                    color={'secondary'}
                    size={'small'}
                    onClick={handleDialogClose}
                    children={<CloseIcon sx={{ color: '#707070' }} key={'closeIcon'} />}
                  />
                }
              />,
              <ETIDialogBody
                key={'etiDialogBody'}
                sx={{ padding: '8px 12px', minWidth: '550px' }}
                dividers={true}
                children={[
                  <DropSelect
                    key={'dropSelect'}
                    options={availableOptions}
                    label={'Select a program'}
                    placeholder={'Choose a program'}
                    value={selectedProgram}
                    onChange={(value: string) => {
                      setSelectedProgram(value);
                    }}
                    fullWidth={true}
                    required={false}
                  />,
                ]}
              />,
              <ETIDialogFooter
                key={'etiDialogFooter'}
                children={[
                  <Button
                    key={'etiDialogFooterCancelBtn'}
                    color='secondary'
                    onClick={handleDialogClose}>
                    CANCEL
                  </Button>,
                  <Button
                    key={'etiDialogFooterSaveBtn'}
                    children={'OPEN'}
                    onClick={handleDialogSave}
                  />,
                ]}
              />,
            ]}
          />
        </div>
        {notificationType === 'marquee' && (
          <Marquee
            style={{
              fontSize: '24px',
              overflow: 'unset',
              boxShadow: '0 -4px 6px -2px rgba(0, 0, 0, 0.1)',
              zIndex: '999999999',
              background: 'white',
            }}
            autoFill={false}>
            {marquee.length ? (
              marquee.map((message, index) => (
                <span key={'marqueeMsg' + message.pvDataID + index} className='mr-32'>
                  {message.message}{' '}
                </span>
              ))
            ) : (
              <span>No updates</span>
            )}
          </Marquee>
        )}
      </>
    );
  }
);

export default GridLayout;
